import { useEffect } from 'react';

const SITE = 'https://diligentsia.co.uk';

/**
 * Sets document title, meta description, canonical URL and JSON-LD schema blocks for a page,
 * and restores/cleans them up on unmount. Needed because the app is a client-rendered SPA
 * with no per-route head management.
 */
export function useDocumentHead({ title, description, canonicalPath, schema = [] }) {
    useEffect(() => {
        const prevTitle = document.title;
        if (title) document.title = title;

        // Meta description
        let metaDesc = document.querySelector('meta[name="description"]');
        const createdMeta = !metaDesc;
        if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
        }
        const prevDesc = metaDesc.getAttribute('content');
        if (description) metaDesc.setAttribute('content', description);

        // Canonical link
        let canonical = document.querySelector('link[rel="canonical"]');
        const createdCanonical = !canonical;
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        const prevCanonical = canonical.getAttribute('href');
        if (canonicalPath) canonical.setAttribute('href', `${SITE}${canonicalPath}`);

        // JSON-LD schema blocks
        const scripts = (schema || []).map((block) => {
            const s = document.createElement('script');
            s.type = 'application/ld+json';
            s.setAttribute('data-aeo-schema', 'true');
            s.text = JSON.stringify(block);
            document.head.appendChild(s);
            return s;
        });

        return () => {
            document.title = prevTitle;
            if (createdMeta) metaDesc.remove();
            else if (prevDesc != null) metaDesc.setAttribute('content', prevDesc);
            if (createdCanonical) canonical.remove();
            else if (prevCanonical != null) canonical.setAttribute('href', prevCanonical);
            scripts.forEach(s => s.remove());
        };
        // Reason: serialize schema so the effect re-runs only when the page actually changes.
    }, [title, description, canonicalPath, JSON.stringify(schema)]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default useDocumentHead;
